import type { Label, LabelGroup } from "Stores/labelStore";

import type {
    CreateLabelGroupRequest,
    CreateLabelRequest,
    LabelDto,
    LabelGroupDto,
    LabelSummaryDto1,
    UpdateLabelGroupRequest,
    UpdateLabelRequest
} from "./client";

/**
 * Maps a flat-list `LabelDto` onto the legacy `Label`. Its `labelGroup` is a
 * summary without `labels`, so the group's label list is left empty.
 */
export function labelDtoToLabel(dto: LabelDto): Label {
    const group = dto.labelGroup;

    return {
        id: dto.id ?? 0,
        name: dto.name,
        school_id: dto.schoolId,
        // Legacy treats a missing flag as active, and the ticket form filters on `=== false`.
        is_archived: dto.isArchived ?? false,
        label_group: group
            ? {id: group.id ?? 0, name: group.name, ordering: group.ordering ?? 0, labels: []}
            : undefined
    };
}

function labelSummaryToLabel(dto: LabelSummaryDto1, group: LabelGroup): Label {
    return {
        id: dto.id ?? 0,
        name: dto.name,
        school_id: dto.schoolId,
        is_archived: dto.isArchived ?? false,
        label_group: group
    };
}

/** Maps a `LabelGroupDto` onto the legacy `LabelGroup`, nesting its labels. */
export function labelGroupDtoToLabelGroup(dto: LabelGroupDto): LabelGroup {
    const group: LabelGroup = {
        id: dto.id ?? 0,
        name: dto.name,
        ordering: dto.ordering ?? 0,
        labels: []
    };

    group.labels = (dto.labels ?? []).map(label => labelSummaryToLabel(label, group));

    return group;
}

/** The fields a label write carries. `label_group` may be the group or its id. */
export interface LabelWriteFields {
    name?: string
    label_group?: number | Pick<LabelGroup, "id"> | null
    is_archived?: boolean
}

export function labelGroupId(value: LabelWriteFields["label_group"]): number | undefined {
    if (value === null || value === undefined) {
        return undefined;
    }

    return typeof value === "number" ? value : value.id;
}

export function labelToCreateRequest(fields: LabelWriteFields): CreateLabelRequest {
    return {
        name: fields.name ?? null,
        labelGroup: labelGroupId(fields.label_group) ?? null
    };
}

/** Omits unset fields so they are left alone rather than sent as null. */
export function labelToUpdateRequest(fields: LabelWriteFields): UpdateLabelRequest {
    const body: UpdateLabelRequest = {};
    const groupId = labelGroupId(fields.label_group);

    if (fields.name !== undefined) {
        body.name = fields.name;
    }
    if (groupId !== undefined) {
        body.labelGroup = groupId;
    }
    if (fields.is_archived !== undefined) {
        body.isArchived = fields.is_archived;
    }

    return body;
}

/** The legacy create body names the ordering field `order`. */
export interface LabelGroupWriteFields {
    name?: string
    order?: number
    ordering?: number
}

export function labelGroupToCreateRequest(fields: LabelGroupWriteFields): CreateLabelGroupRequest {
    return {
        name: fields.name ?? null,
        ordering: fields.ordering ?? fields.order
    };
}

export function labelGroupToUpdateRequest(fields: LabelGroupWriteFields): UpdateLabelGroupRequest {
    const body: UpdateLabelGroupRequest = {};
    const ordering = fields.ordering ?? fields.order;

    if (fields.name !== undefined) {
        body.name = fields.name;
    }
    if (ordering !== undefined) {
        body.ordering = ordering;
    }

    return body;
}
